import { ButtonBox, Flex, Loader, OutsideClick, Paginator, Text } from '@upsales/components';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import RequestBuilder from 'Resources/RequestBuilder';
import ComparisonTypes from 'Resources/ComparisonTypes';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import FileStorageApps from 'Components/FileStorageApps';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FoldersResource from 'Resources/FileFolder';
import FileResource from 'Resources/File';
import Client from 'App/resources/Model/Client';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import FolderModal from './FolderModal';
import logError from 'Helpers/logError';
import { DndProvider } from 'react-dnd';
import GridView from './GridView';
import ListView from './ListView';
import Header from './Header';
import NotificationService from 'App/babel/NotificationService';
import './AccountFiles.scss';
import AppService from 'App/legacy/AppService';
import { buildFileRequest, buildMeetingsCountRequest, isMeetingsFolder, MEETINGS_FOLDER_ID } from './fileRequests';
import EventBus from 'App/services/EventBus';

export type FolderType = {
	id: number;
	name: string;
	folderId: number | null;
	isSystem?: boolean;
};

export type FolderTreeType = {
	id: number;
	name: string;
};

export type FileType = {
	client: { id: number };
	id: number;
	filename: string;
	size: number;
	extension: string;
	uploadDate: string;
	user: { id: number };
	driveFolderId: number;
	entityId?: number;
	entity?: string;
	userRemovable: boolean;
};

export type SortState = {
	field: string;
	asc: boolean;
};

type AccountFilesProps = {
	client: Client;
	subAccountIds?: number[];
};

const PAGE_SIZE = 50;
const EMPTY_FOLDER_RESULT = { data: [], metadata: { total: 0, offset: 0, limit: PAGE_SIZE } };

const countMeetingFiles = (clientIds: number[]): Promise<number> =>
	FileResource.find(buildMeetingsCountRequest(clientIds).build()).then(
		(res: { metadata: { total: number } }) => res.metadata.total,
		(e: unknown) => {
			logError(e, 'Could not count meeting files');
			// The top level leaves meeting files out, so an unknown count shows the folder rather than hide them.
			return 1;
		}
	);

const AccountFiles = ({ client, subAccountIds = [] }: AccountFilesProps) => {
	const classes = new BemClass('AccountFiles');

	const hasReactUploadFileModal = hasSoftDeployAccess('REACT_UPLOAD_FILE_MODAL');
	const hasMeetingsFolder = hasSoftDeployAccess('DOCUMENTS_MEETINGS_FOLDER');

	const [folders, setFolders] = useState<FolderType[]>([]);
	const [files, setFiles] = useState<FileType[]>([]);
	const [fileMeta, setFileMeta] = useState<{ total: number; offset: number; limit: number } | null>(null);
	const [folderMeta, setFolderMeta] = useState<{ total: number; offset: number; limit: number } | null>(null);
	const [folderTree, setFolderTree] = useState<FolderTreeType[]>([]);
	const [listMode, setListMode] = useState(true);
	const [initialLoadDone, setInitialLoadDone] = useState(false);
	const [showAddFolderModal, setShowAddFolderModal] = useState(false);
	const [searchString, setSearchString] = useState<string | null>(null);
	const [sort, setSort] = useState<SortState>({ field: 'filename', asc: true });
	const [currentOffset, setCurrentOffset] = useState(0);
	const [folderOffset, setFolderOffset] = useState(0);

	// Use subAccountIds from props (provided by parent controller which respects user's include subaccounts setting)
	// Memoize to prevent unnecessary re-fetches when props haven't changed
	const clientIds = useMemo(() => [client.id, ...subAccountIds], [client.id, subAccountIds]);

	const currentFolderId = folderTree[folderTree.length - 1]?.id ?? null;
	const isInMeetingsFolder = isMeetingsFolder(currentFolderId);
	const fetchIdRef = useRef(0);

	const fetchFolderContents = useCallback(async () => {
		if (!client.id) return;

		const fetchId = ++fetchIdRef.current;

		const isSearching = searchString !== null && searchString.length > 0;
		const showMeetingsFolder = hasMeetingsFolder && !isSearching && currentFolderId === null && folderOffset === 0;

		const folderRb = new RequestBuilder();
		folderRb.addFilter({ field: 'clientId' }, ComparisonTypes.Equals, clientIds);
		if (isSearching) {
			folderRb.addFilter({ field: 'name' }, ComparisonTypes.Wildcard, searchString);
		} else {
			folderRb.addFilter({ field: 'folderId' }, ComparisonTypes.Equals, currentFolderId ?? null);
		}
		folderRb.addSort('name', true);
		folderRb.limit = PAGE_SIZE;
		folderRb.offset = folderOffset;

		const fileRb = buildFileRequest({
			clientIds,
			searchString: isSearching ? searchString : null,
			currentFolderId,
			hasMeetingsFolder
		});
		fileRb.addSort(sort.field, sort.asc);
		fileRb.limit = PAGE_SIZE;
		fileRb.offset = currentOffset;

		try {
			const [folderRes, fileRes, meetingFileCount] = await Promise.all([
				isMeetingsFolder(currentFolderId) && !isSearching
					? EMPTY_FOLDER_RESULT
					: FoldersResource.find(folderRb.build()),
				FileResource.find(fileRb.build()),
				showMeetingsFolder ? countMeetingFiles(clientIds) : 0
			]);

			if (fetchId !== fetchIdRef.current) return;

			const meetingsFolder: FolderType[] = meetingFileCount
				? [{ id: MEETINGS_FOLDER_ID, name: T('default.appointments'), folderId: null, isSystem: true }]
				: [];
			setFolders([...meetingsFolder, ...folderRes.data]);
			setFolderMeta(folderRes.metadata);
			setFiles(fileRes.data);
			setFileMeta(fileRes.metadata);
		} catch (e) {
			logError(e, 'Could not fetch files and folders');
		} finally {
			if (fetchId === fetchIdRef.current) {
				setInitialLoadDone(true);
			}
		}
	}, [client.id, clientIds, currentFolderId, searchString, sort, currentOffset, folderOffset, hasMeetingsFolder]);

	useEffect(() => {
		fetchFolderContents();
	}, [fetchFolderContents]);

	useEffect(() => {
		if (localStorage.getItem('boxViewDocuments') === '1') {
			setListMode(false);
		}
	}, []);

	useEffect(() => {
		const listener = EventBus.on('file.uploaded', () => {
			fetchFolderContents();
		});
		return () => listener();
	}, [fetchFolderContents]);

	const buildFolderTree = (folderId: number) => {
		const folderRb = new RequestBuilder();
		folderRb.addFilter({ field: 'clientId' }, ComparisonTypes.Equals, clientIds);

		FoldersResource.find(folderRb.build())
			.then((res: { data: FolderType[] }) => {
				const allFolders = res.data;
				const tree: FolderTreeType[] = [];
				let id: number | null = folderId;
				while (id) {
					const parentFolder = allFolders.find(folder => folder.id === id);
					if (parentFolder) {
						tree.unshift({ id: parentFolder.id, name: parentFolder.name });
						id = parentFolder.folderId;
					} else {
						break;
					}
				}
				setFolderTree(tree);
				setSearchString(null);
			})
			.catch((e: unknown) => logError(e, 'Could not build folder tree'));
	};

	const openFolder = (folderId: number, name: string) => {
		if (isMeetingsFolder(folderId)) {
			setFolderTree([{ id: folderId, name }]);
			setSearchString(null);
		} else if (searchString !== null && folderId) {
			buildFolderTree(folderId);
			return;
		} else {
			setFolderTree((currentTree: FolderTreeType[]) => [...currentTree, { id: folderId, name }]);
		}
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const changeFolderFromTree = (folderId?: number | null) => {
		if (folderId === currentFolderId) return;

		setFolderTree((currentTree: FolderTreeType[]) => {
			const indexOfFolder = currentTree.findIndex(folder => folder.id === folderId);
			const newfolderTree = [...currentTree];
			newfolderTree.length = indexOfFolder + 1;
			return newfolderTree;
		});
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const createFolder = (name: string) => {
		if (!client.id) return;

		FoldersResource.save({ name, clientId: client.id, folderId: currentFolderId })
			.then(() => {
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderCreated'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not create folder');
				NotificationService.add({
					style: NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderCreateFailed'
				});
			});
	};

	const changeFolderFolder = (id: number, folderId: number | null) => {
		if (!client.id || isMeetingsFolder(id) || isMeetingsFolder(folderId)) return;

		FoldersResource.save({ clientId: client.id, id, folderId })
			.then(() => {
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderMoved'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not move folder');
				NotificationService.add({
					style: NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderMoveFailed'
				});
			});
	};

	const changeFileFolder = (id: number, folderId: number | null) => {
		if (isMeetingsFolder(folderId)) return;

		FoldersResource.changeFileFolder(id, folderId)
			.then(() => {
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'file.fileMoved'
				});
				fetchFolderContents();
			})
			.catch(e => {
				logError(e, 'Could not move file');
				NotificationService.add({
					style: NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'file.fileMoveFailed'
				});
			});
	};

	const addFile = () => {
		if (!client.id) return;

		if (hasReactUploadFileModal) {
			return openModal('UploadFileModal', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			});
		}
		Tools.$upModal
			.open('uploadFile', {
				accountId: client.id,
				folderId: currentFolderId,
				closeOnUpload: true
			})
			.catch(e => logError(e, 'Could not upload file'));
	};

	const deleteFolder = (id: number) => {
		FoldersResource.delete(id)
			.then(() => {
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderDeleted'
				});
				fetchFolderContents();
			})
			.catch((err: { response?: { status: number } }) => {
				if (err.response?.status === 400) {
					NotificationService.add({
						style: NotificationService.style.ERROR,
						icon: 'times',
						title: 'default.error',
						body: 'foler.folderNotEmpty'
					});
				} else {
					logError(err, 'Could not delete folder');
				}
			});
	};

	const deleteFile = (file: FileType) => {
		Tools.File.delete(file, { noConfirm: true })
			.then(res => {
				if (!res) return;
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not delete file');
				NotificationService.add({
					style: NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'deleteError.file'
				});
			});
	};

	const changeNameOfFolder = (id: number, name: string) => {
		if (!client.id) return;

		FoldersResource.save({ name, clientId: client.id, id })
			.then(() => {
				setFolderTree((currentFolderTree: FolderTreeType[]) =>
					currentFolderTree.map((folder: FolderTreeType) => {
						if (folder.id === id) {
							return { ...folder, name };
						}
						return folder;
					})
				);
				NotificationService.add({
					style: NotificationService.style.SUCCESS,
					icon: 'check',
					title: 'default.done',
					body: 'folder.folderNameChanged'
				});
				fetchFolderContents();
			})
			.catch((e: unknown) => {
				logError(e, 'Could not change name');
				NotificationService.add({
					style: NotificationService.style.ERROR,
					icon: 'times',
					title: 'default.error',
					body: 'folder.folderNameChangeFailed'
				});
			});
	};

	const onSortChange = (newSort: SortState) => {
		setSort(newSort);
		setCurrentOffset(0);
		setFolderOffset(0);
	};

	const content = () => {
		if (!files.length && !folders.length) {
			let element;
			if (searchString || isInMeetingsFolder) {
				element = <Text size="lg">{T('noResult.generic')}</Text>;
			} else if (showAddFolderModal) {
				element = (
					<OutsideClick outsideClick={() => setShowAddFolderModal(false)} targetClass={'FolderModal'}>
						<div>
							<FolderModal
								animate
								noDropDown
								onClick={name => {
									createFolder(name);
									setShowAddFolderModal(false);
								}}
							/>
						</div>
					</OutsideClick>
				);
			} else {
				element = (
					<>
						<ButtonBox selectable={false} onClick={addFile} icon="file" title={T('file.addFile')} />
						<ButtonBox
							selectable={false}
							onClick={() => setShowAddFolderModal(true)}
							icon="folder"
							title={T('file.addFolder')}
						/>
					</>
				);
			}

			return (
				<Flex className={classes.elem('center').b()} gap="u10" justifyContent="center" alignItems="center">
					{element}
				</Flex>
			);
		}

		const folderPaginatorEl =
			folderMeta && folderMeta.total > PAGE_SIZE ? (
				<Paginator
					limit={PAGE_SIZE}
					offset={folderOffset}
					total={folderMeta.total}
					onChange={setFolderOffset}
					align="center"
				/>
			) : null;

		const filesViewProps = {
			folders,
			files,
			openFolder,
			deleteFolder,
			deleteFile,
			changeFolderFolder,
			changeFileFolder,
			changeNameOfFolder,
			clientId: client.id,
			sort,
			onSortChange,
			folderPaginator: folderPaginatorEl
		};

		if (listMode) {
			return <ListView {...filesViewProps} />;
		}
		return <GridView {...filesViewProps} filesDraggable={!isInMeetingsFolder || !!searchString} />;
	};

	if (!initialLoadDone) {
		return (
			<Flex className={classes.elem('center-loader').b()} justifyContent="center" alignItems="center">
				<Loader size="xl" />
			</Flex>
		);
	}
	return (
		<div className={classes.b()}>
			<DndProvider backend={HTML5Backend}>
				<Header
					folders={folderTree}
					addFolder={createFolder}
					addFile={addFile}
					changeFolder={changeFolderFromTree}
					setSearchString={setSearchString}
					searchString={searchString}
					setListMode={setListMode}
					listMode={listMode}
					changeFolderFolder={changeFolderFolder}
					changeFileFolder={changeFileFolder}
					numberOfFolders={folders.length}
					numberOfFiles={fileMeta?.total ?? files.length}
					inMeetingsFolder={isInMeetingsFolder}
				/>
				{content()}
				{fileMeta && fileMeta.total > PAGE_SIZE ? (
					<Paginator
						limit={PAGE_SIZE}
						offset={currentOffset}
						total={fileMeta.total}
						onChange={setCurrentOffset}
						align="center"
					/>
				) : null}
				{AppService.getFileStorageIntegrations().length > 0 ? (
					<div className={classes.elem('google-drive-container').b()}>
						<FileStorageApps
							apps={AppService.getFileStorageIntegrations()}
							objectId={client.id}
							type="client"
							displayTypeFromParent={listMode ? 'list' : 'grid'}
						/>
					</div>
				) : null}
			</DndProvider>
		</div>
	);
};

export default AccountFiles;
